import { motion, useInView } from "framer-motion";
import { useRef } from "react";

export default function Manifesto() {
  const ref1 = useRef(null);
  const isInView1 = useInView(ref1, { once: true, margin: "-80px" });
  const ref2 = useRef(null);
  const isInView2 = useInView(ref2, { once: true, margin: "-80px" });
  const ref3 = useRef(null);
  const isInView3 = useInView(ref3, { once: true, margin: "-80px" });
  const ref4 = useRef(null);
  const isInView4 = useInView(ref4, { once: true, margin: "-80px" });

  return (
    <>
      {/* ── The Problem ── */}
      <section
        id="manifesto"
        data-testid="manifesto-section"
        className="py-24 sm:py-32 bg-cream"
      >
        <div ref={ref1} className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView1 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mb-16 sm:mb-20"
          >
            <p className="font-body text-sm uppercase tracking-[0.25em] text-terra mb-5" data-testid="problem-label">
              The Problem
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink leading-snug">
              In Lagos, loving books is easy. Getting books is hard.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {[
              {
                title: "Books are expensive",
                body: "New books can cost as much as a week's transport, a solid data plan, or a meaningful chunk of someone's monthly budget. For most readers, buying freely is not realistic.",
              },
              {
                title: "Access is patchy",
                body: "The best bookstores are concentrated in a few parts of the city. If you do not live nearby, have flexible time, or can comfortably move around Lagos, access gets harder fast.",
              },
              {
                title: "Discovery is scattered",
                body: "Readers, book clubs, and bookstores are spread across Instagram, WhatsApp, X, and random DMs. Finding the right book or the right people is messy, fragmented, and unreliable.",
              },
              {
                title: "Borrowing and swapping are still informal",
                body: "There is no simple, trusted way to borrow, swap, resell, or rent books across the city. Great books sit idle on shelves while readers who want them cannot easily find, afford, or access them.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 25 }}
                animate={isInView1 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + 0.12 * i }}
                data-testid={`problem-card-${i}`}
                className="relative pl-6 border-l-2 border-cream-dark"
              >
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">
                  {item.title}
                </h3>
                <p className="font-body text-base text-ink-light leading-relaxed">
                  {item.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Manifesto ── */}
      <section
        data-testid="manifesto-beliefs-section"
        className="relative py-28 sm:py-36 lg:py-44 overflow-hidden"
      >
        <div className="absolute inset-0 bg-forest">
          <img
            src="https://images.unsplash.com/photo-1664253394380-783cffcaf490?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxib29rcyUyMGNvZmZlZSUyMHRhYmxlfGVufDB8fHx8MTc3MjU2NzgzM3ww&ixlib=rb-4.1.0&q=85"
            alt="Books and coffee"
            className="w-full h-full object-cover opacity-10 mix-blend-luminosity"
          />
        </div>

        <div ref={ref2} className="relative z-10 max-w-4xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView2 ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="font-body text-sm uppercase tracking-[0.3em] text-gold mb-12 text-center"
            data-testid="manifesto-label"
          >
            What We Believe
          </motion.p>

          <div className="space-y-12 sm:space-y-16">
            {[
              {
                number: "01",
                heading: "Lagos already has a reading culture.",
                body: "The problem is not demand. The problem is cost, access, and visibility. Readers are here. Book clubs are here. Bookstores are here. They are just too disconnected from one another.",
              },
              {
                number: "02",
                heading: "Books should move.",
                body: "From shelf to shelf. From reader to reader. From bookstore to book club and back again. A good book should not stop at one owner if there is another reader waiting for it.",
              },
              {
                number: "03",
                heading: "Discovery should be social and local.",
                body: "The best recommendations do not come from distant algorithms. They come from people like you, in your city, who know the stores, the clubs, the genres, and the reading mood of Lagos.",
              },
              {
                number: "04",
                heading: "Access matters as much as ownership.",
                body: "Not every reader needs to buy every book new. Borrowing, swapping, renting, and buying second-hand are not backup options. They are core ways a healthy reading culture grows.",
              },
              {
                number: "05",
                heading: "When the network connects, the culture grows.",
                body: "When the right readers, books, bookstores, and book clubs can find one another easily, more books move, more ideas circulate, and the city becomes easier to read in.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView2 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.15 * i }}
                data-testid={`manifesto-belief-${i}`}
                className="flex gap-6 sm:gap-10"
              >
                <span className="font-accent text-3xl sm:text-4xl text-gold/30 font-light leading-none flex-shrink-0 pt-1">
                  {item.number}
                </span>
                <div>
                  <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl font-semibold text-cream/95 italic mb-3 leading-snug">
                    {item.heading}
                  </h3>
                  <p className="font-body text-base sm:text-lg text-cream/60 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView2 ? { scaleX: 1 } : {}}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-16 mx-auto w-32"
          >
            <div className="h-1 bg-gradient-to-r from-terra via-gold to-terra rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* ── The Vision / Solution ── */}
      <section data-testid="vision-section" className="py-24 sm:py-32 bg-cream">
        <div ref={ref3} className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView3 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 sm:mb-20 max-w-3xl mx-auto"
          >
            <p className="font-body text-sm uppercase tracking-[0.25em] text-terra mb-5">
              The Vision
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink leading-snug mb-6">
              A Lagos where every book finds its next reader
            </h2>
            <p className="font-body text-base sm:text-lg text-ink-light leading-relaxed">
              A city where it is effortless to get your next great book, whether you buy it,
              borrow it, swap for it, or discover it through people you trust.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              {
                label: "Access Across The City",
                title: "Get books from any neighbourhood.",
                body: "A more connected Lagos where distance, scattered supply, and limited store access no longer decide what you get to read next.",
                accent: "terra",
              },
              {
                label: "One Connected Ecosystem",
                title: "Readers, clubs, and stores in one room.",
                body: "Book lovers, book clubs, and bookstores become part of the same city-wide network instead of existing as isolated pockets across apps, chats, and chance encounters.",
                accent: "forest",
              },
              {
                label: "Books In Motion",
                title: "Buy, borrow, swap, repeat.",
                body: "A reading culture where books circulate instead of sitting idle, and every good title has more than one life in the hands of Lagos readers.",
                accent: "gold",
              },
              {
                label: "Local Discovery",
                title: "Recommendations that feel close to home.",
                body: "A city where your next favorite book comes from trusted readers, nearby clubs, and local bookstores, not just whichever global platform shouts the loudest.",
                accent: "ink",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView3 ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.15 * i }}
                data-testid={`vision-pillar-${i}`}
                className={`relative bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-cream-dark/80 hover:shadow-md transition-all duration-500 group overflow-hidden`}
              >
                <div
                  className={`absolute top-0 left-0 w-full h-1 ${
                    item.accent === "terra" ? "bg-terra" :
                    item.accent === "forest" ? "bg-forest" :
                    item.accent === "gold" ? "bg-gold" :
                    "bg-ink"
                  }`}
                />
                <p className={`font-body text-xs uppercase tracking-[0.2em] mb-3 ${
                  item.accent === "terra" ? "text-terra" :
                  item.accent === "forest" ? "text-forest" :
                  item.accent === "gold" ? "text-gold" :
                  "text-ink-muted"
                }`}>
                  {item.label}
                </p>
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3 leading-snug">
                  {item.title}
                </h3>
                <p className="font-body text-base text-ink-light leading-relaxed">
                  {item.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The Mission Statement ── */}
      <section data-testid="mission-section" className="relative py-24 sm:py-28 overflow-hidden bg-ink">
        <div ref={ref4} className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView4 ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1 }}
          >
            <p className="font-body text-sm uppercase tracking-[0.3em] text-gold mb-10">
              The Mission
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium italic text-cream/90 leading-relaxed mb-8">
              To connect Lagos readers, book clubs, and bookstores in one platform for buying, borrowing, swapping, and discovering books.
            </h2>
            <p className="font-body text-base sm:text-lg text-cream/50 leading-relaxed">
              One place to buy and sell new and second-hand books, borrow and swap with trusted people nearby, and track the reading life that keeps the city talking.
            </p>
          </motion.div>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView4 ? { scaleX: 1 } : {}}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-12 mx-auto w-24"
          >
            <div className="h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent rounded-full" />
          </motion.div>
        </div>
      </section>
    </>
  );
}
